"use client";

import type { LessonDetails } from "@/features/workspaces/types/lessonDetails.ts";
import type { AvailableRoles } from "@/types";

import { motion } from "motion/react";
import { Icon } from "@iconify-icon/react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { Badge } from "@/components/ui/badge.tsx";
import { Button } from "@/components/ui/button.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { STUDENT_WORKSPACE_PATHS, TUTOR_WORKSPACE_PATHS } from "@/routes/paths.ts";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";

interface LessonParticipantsCardProps {
  lessonDetails: LessonDetails;
}

export const LessonParticipantsCard = ({ lessonDetails }: LessonParticipantsCardProps) => {
  const { t } = useTranslation(["workspaces", "common"]);
  const { workspaceId } = useParams();
  const { user: currentUser } = useUserStore();
  const { data: workspaceData } = useWorkspaceInformationStore();

  const getChatPath = () => {
    if (!workspaceId || !currentUser || !workspaceData?.main_conversation_id) return "#";

    const basePath =
      currentUser.role === "tutor"
        ? TUTOR_WORKSPACE_PATHS(workspaceId).CHAT
        : STUDENT_WORKSPACE_PATHS(workspaceId).CHAT;

    return `${basePath}/${workspaceData.main_conversation_id}`;
  };

  return (
    <motion.div
      animate={{ opacity: 1, y: 0 }}
      className="col-span-12 lg:col-span-4"
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: 0.4 }}
    >
      <div className="bg-foreground/50 text-accent-foreground rounded-2xl border-none p-4 shadow-2xl sm:p-6">
        <h3 className="mb-4 flex items-center gap-3 text-lg font-bold sm:mb-6 sm:text-xl">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-500/20 sm:h-10 sm:w-10">
            <Icon className="h-4 w-4 text-purple-400 sm:h-5 sm:w-5" icon="mdi:account-group" />
          </div>
          {t("workspaces:participants", "Participants")}
        </h3>

        <div className="space-y-3 sm:space-y-4">
          {lessonDetails.participants.map((participant, index) => (
            <motion.div
              key={participant.id}
              animate={{ opacity: 1, y: 0 }}
              className="bg-muted/20 hover:bg-muted/30 rounded-xl p-3 backdrop-blur-sm transition-all sm:p-4"
              initial={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2, delay: index * 0.1 }}
            >
              <div className="flex items-center gap-3 sm:gap-4">
                <Avatar className="h-10 w-10 border-2 border-white/20 sm:h-12 sm:w-12">
                  <AvatarImage
                    alt={participant.user.name}
                    src={participant.user.avatar || "/placeholder.svg"}
                  />
                  <AvatarFallback className="bg-muted/40 text-accent-foreground text-sm sm:text-base">
                    {participant.user.name
                      .split(" ")
                      .map((n) => n[0])
                      .join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium sm:text-base">
                    {participant.user.name}
                  </p>
                  <div className="mt-1 flex items-center gap-2 sm:mt-2">
                    <Badge
                      className="bg-muted/30 text-accent-foreground rounded-full border-white/20 px-2 py-0.5 text-xs font-medium sm:px-3 sm:py-1"
                      variant="outline"
                    >
                      {t(`common:roles.${participant.type as AvailableRoles}`)}
                    </Badge>
                  </div>
                </div>
                {currentUser &&
                  participant.user.id !== currentUser.id &&
                  workspaceData?.main_conversation_id && (
                    <Button
                      asChild
                      className="hover:bg-muted/40 transition-colors"
                      size="icon"
                      variant="ghost"
                    >
                      <Link title={t("common:messages", "Messages")} to={getChatPath()}>
                        <Icon
                          className="text-muted-foreground"
                          height={"1rem"}
                          icon="mdi:message-outline"
                          width={"1rem"}
                        />
                      </Link>
                    </Button>
                  )}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
};
